import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/forgot-password', { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-orange-50 dark:from-stone-950 dark:to-stone-900 p-4">
        <Card className="w-full max-w-md border-border shadow-xl" data-testid="reset-email-sent-card">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <CardTitle className="text-2xl font-outfit">Check Your Email</CardTitle>
            <CardDescription>
              If an account exists with <span className="font-medium text-foreground">{email}</span>, 
              we've sent a password reset link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full rounded-full" data-testid="back-to-login">
                <ArrowLeft size={16} className="mr-2" />
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-50 to-orange-50 dark:from-stone-950 dark:to-stone-900 p-4">
      <Card className="w-full max-w-md border-border shadow-xl" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-orange-600" />
          </div>
          <CardTitle className="text-2xl font-outfit">Forgot Password?</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="forgot-email-input"
                className="mt-1"
              />
            </div>

            {error && (
              <p className="text-sm text-error bg-error/10 dark:bg-red-900/20 p-3 rounded-lg" data-testid="forgot-error">
                {error}
              </p>
            )}

            <Button 
              type="submit" 
              className="w-full rounded-full" 
              disabled={loading}
              data-testid="send-reset-link-button"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-link">
                <ArrowLeft size={16} className="mr-2" />
                Back to Login
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
